import type { FilterOption, FilterOptionValue } from 'features/filters/types'
import { FilterTypes } from 'features/filters/types'
import { ALIAS_FOR_DELETED_VALUES } from 'src/constants/filter-values'

/**
 * Is this stored value an opaque identifier rather than something readable?
 *
 * The filter picker stores theme selections as cluster uuids, so an
 * unresolvable one has no name to show — printing the raw id tells the user
 * nothing. Name-shaped values (older and admin-seeded configs) still read
 * fine and are shown as they are.
 */
export function looksLikeId(value: string): boolean {
  // Matches what the server's `uuid.UUID()` accepts, not just the canonical
  // form: braced, urn-prefixed and hyphenless spellings all occur in stored
  // configs. Recognising fewer shapes than the server does would send one of
  // them down the name-shaped branch, where it is judged against the options
  // and a LIVE theme can render as no longer available.
  const bare = value
    .trim()
    .replace(/^urn:uuid:/i, '')
    .replace(/^\{|\}$/g, '')
    .replace(/-/g, '')
  return /^[0-9a-f]{32}$/i.test(bare)
}

/**
 * What to SHOW for an unresolvable value. Matches the main filter widget,
 * which has rendered these as `[Deleted Value]` for a while — same synthetic
 * row, same preserved identifier. Never what to store: the picker
 * toggles on `identifiers`, which keep the original value, so this label
 * cannot round-trip into the saved config. Deliberately not the app's
 * `No Value` alias — that one means the underlying datum is SQL NULL and is
 * required to round-trip as IS_NULL, which would turn a filter on a missing
 * theme into a filter on posts that have none.
 */
export function unavailableLabel(value: string): string {
  return looksLikeId(value) ? ALIAS_FOR_DELETED_VALUES : value
}

export type FilterListItem =
  | { type: 'header'; label: string }
  | { type: 'item'; value: FilterOptionValue }

/**
 * Collapse a field's option values by display value.
 *
 * The same display value can arrive several times with different cluster
 * uuids (one topic living under several child projects), so the uuids
 * accumulate into `identifiers` and the picker matches on any of them.
 */
export function dedupeOptionValues(
  selectedFilter: FilterOption | undefined,
): FilterOptionValue[] {
  if (!selectedFilter || selectedFilter.filter_type !== FilterTypes.SELECT)
    return []
  const deduped: Record<string, { n_posts: number; identifiers: string[] }> = {}
  for (const item of selectedFilter.values) {
    if (!deduped[item.value])
      deduped[item.value] = { n_posts: 0, identifiers: [] }
    deduped[item.value]!.n_posts += item.n_posts ?? 0
    deduped[item.value]!.identifiers.push(item.cluster_uuid ?? item.value)
  }
  return Object.entries(deduped).map(([key, val]) => ({
    value: key,
    n_posts: val.n_posts,
    identifiers: val.identifiers,
  }))
}

/**
 * Selected values the current options no longer offer.
 *
 * Without these the value is invisible AND permanent: the list is built only
 * from live options, so a selection the project's data no longer has gets no
 * row, and with no row there is nothing to untick. It then rides along on
 * every save, which also means re-picking the visible values reproduces the
 * stored config exactly and the form never goes dirty — a saved filter that
 * cannot be edited or removed from the panel at all.
 *
 * Synthesised as ordinary items so they toggle off like any other: the value
 * is its own identifier, and `n_posts` is left undefined because there is no
 * count to show rather than a count of zero.
 *
 * Returns nothing when there is no option set yet. Options load
 * asynchronously, and treating every selection as missing on the way there
 * would flash the whole config as broken.
 *
 * Only judges values it CAN judge. A cluster uuid absent from the options is
 * not evidence of anything: the options list a topic once per dataset but not
 * every cluster carrying it, while a stored uuid is resolved through the
 * cluster catalog and compared by topic at query time. So a uuid whose topic
 * is offered under a sibling uuid works perfectly and would be reported here
 * as missing. Measured against one production agent: five of its twenty theme
 * uuids fail this membership test and four of those five are live. The server
 * resolves through the catalog and flags exactly one, so a uuid is judged by
 * the verdict passed in here — never by membership in the options.
 */
export function unavailableSelections(
  selectedFilter: FilterOption | undefined,
  dedupedItems: FilterOptionValue[],
  selectedFilterValues: string[],
  staleStoredValues?: readonly string[],
  staleValueLabels?: Readonly<Record<string, string>>,
): FilterOptionValue[] {
  if (!selectedFilter || selectedFilter.filter_type !== FilterTypes.SELECT)
    return []

  // The server's verdict when there is one — it resolves each stored value
  // through the cluster catalog, the only place a uuid joins to its topic.
  //
  // An EMPTY list is not a verdict. The server's own diagnostic never raises
  // and returns [] when the options cache is cold or upstream is down, which
  // is indistinguishable from "nothing is stale"; so is an older payload with
  // no stored values at all. Treating [] as authoritative would silence the
  // local check too and leave FEWER removable rows than before.
  const flagged = staleStoredValues?.length
    ? new Set(staleStoredValues)
    : undefined

  const offered = new Set(dedupedItems.flatMap((item) => item.identifiers))
  return selectedFilterValues
    .filter((value) =>
      flagged
        ? // Still must be absent from the options, or the value renders twice
          // — once here and once, checked, under Applied.
          flagged.has(value) && !offered.has(value)
        : // No verdict to go on (creating an agent, or an older payload):
          // judge only name-shaped values, which the options enumerate.
          !looksLikeId(value) && !offered.has(value),
    )
    .map((value) => ({
      // The server's name when it has one. Without it the row would read
      // `[Deleted Value]` while the notice above names the very same theme.
      value: staleValueLabels?.[value] ?? unavailableLabel(value),
      identifiers: [value],
    }))
}

/** Group the rows the picker renders, most-actionable group first. */
export function buildFilterListItems(
  dedupedItems: FilterOptionValue[],
  unavailableItems: FilterOptionValue[],
  selectedFilterValues: string[],
  filterSearchTerm: string,
): FilterListItem[] {
  const matchesSearch = (item: FilterOptionValue): boolean =>
    item.value.toLowerCase().includes(filterSearchTerm.toLowerCase())
  const byPosts = (a: FilterOptionValue, b: FilterOptionValue): number =>
    (b.n_posts ?? 0) - (a.n_posts ?? 0)

  const filtered = dedupedItems.filter(matchesSearch)
  const isSelected = (item: FilterOptionValue): boolean =>
    item.identifiers.some((id) => selectedFilterValues.includes(id))

  const unavailable = unavailableItems.filter(matchesSearch)
  const applied = filtered.filter(isSelected).sort(byPosts)
  const unselected = filtered.filter((item) => !isSelected(item)).sort(byPosts)

  const result: FilterListItem[] = []

  // First: it is the only group the user must act on to make the filter
  // saveable, and the one they came here to find.
  if (unavailable.length > 0) {
    result.push({
      type: 'header',
      label: `No longer available (${unavailable.length})`,
    })
    unavailable.forEach((item) => result.push({ type: 'item', value: item }))
  }

  if (applied.length > 0) {
    result.push({ type: 'header', label: `Applied (${applied.length})` })
    applied.forEach((item) => result.push({ type: 'item', value: item }))
  }

  result.push({ type: 'header', label: `Unselected (${unselected.length})` })
  unselected.forEach((item) => result.push({ type: 'item', value: item }))

  return result
}
